import { Component, input, output } from '@angular/core';

import {
  SignInButtonComponent,
  SignOutButtonComponent,
} from '@sandbox-platform/shared-ui';
import { GroupedExpert } from '../store/attendance-tracking.store';

@Component({
  standalone: true,
  imports: [SignInButtonComponent, SignOutButtonComponent],
  selector: 'lib-expert-list',
  template: `
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      @for (expert of experts(); track expert.id) {
      <div
        class="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer relative"
        [class.ring-2]="selectedExpert()?.id === expert.id"
        [class.ring-blue-300]="selectedExpert()?.id === expert.id"
        (click)="onExpertClick(expert)"
      >
        <!-- Floating Status Badge -->
        <div class="absolute -top-2 -right-2 z-10">
          <span
            class="px-2 py-1 text-xs font-medium rounded-full"
            [class]="getSignInStatusBadgeClass(expert)"
          >
            {{ getSignInStatusText(expert) }}
          </span>
        </div>

        <div class="p-4">
          <div class="flex flex-col items-center gap-4">
            <!-- Expert Info -->
            <div class="flex-1 w-full">
              <div class="flex items-center space-x-3">
                <!-- Avatar -->
                <div class="flex-shrink-0">
                  <div
                    class="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm"
                    [class]="getAvatarClass(expert)"
                  >
                    {{ getInitials(expert.first_name, expert.last_name) }}
                  </div>
                </div>

                <!-- Expert Details -->
                <div class="flex-1 min-w-0">
                  <h3 class="text-lg font-medium text-gray-700 truncate">
                    {{ expert.full_name }}
                  </h3>
                  <div
                    class="mt-1 flex flex-col gap-1 text-sm text-gray-500 mb-2"
                  >
                    <div class="flex items-center space-x-4">
                      <span>{{ expert.expert_id }}</span>
                      <span>{{ expert.industry_name }}</span>
                    </div>
                    @if (expert.organization_name) {
                    <div class="text-xs text-gray-400">
                      {{ expert.organization_name }}
                    </div>
                    }
                  </div>

                  <!-- Session Information -->
                  <div class="space-y-1">
                    @if (expert.session_count > 0) {
                    <div class="text-xs text-gray-600">
                      <span class="font-medium">Sessions today:</span>
                      {{ expert.session_count }}
                      @if (expert.active_sessions.length > 0) {
                      <span class="text-green-600 ml-1"
                        >({{ expert.active_sessions.length }} active)</span
                      >
                      }
                    </div>
                    } @if (expert.first_arrival_time) {
                    <div class="text-xs text-green-600">
                      <span class="font-medium">First arrival:</span>
                      {{ formatTime(expert.first_arrival_time) }}
                    </div>
                    } @if (expert.last_departure_time) {
                    <div class="text-xs text-red-600">
                      <span class="font-medium">Last departure:</span>
                      {{ formatTime(expert.last_departure_time) }}
                    </div>
                    } @if (expert.total_work_duration > 0) {
                    <div class="text-xs text-blue-600">
                      <span class="font-medium">Total time:</span>
                      {{ formatDuration(expert.total_work_duration) }}
                    </div>
                    }
                  </div>

                  <!-- Active Sessions Details -->
                  @if (expert.active_sessions.length > 0) {
                  <div class="mt-2 space-y-1">
                    @for (session of expert.active_sessions; track
                    session.session_id) {
                    <div class="bg-green-50 px-2 py-1 rounded text-xs">
                      <span class="text-green-800 font-medium"
                        >Active session:</span
                      >
                      <span class="text-green-700 ml-1">
                        Started {{ formatTime(session.arrival_time) }}
                        @if (session.location) {
                        <span class="text-green-600">
                          - {{ session.location.type }}</span
                        >
                        }
                      </span>
                    </div>
                    }
                  </div>
                  }
                </div>
              </div>
            </div>

            <!-- Status and Actions -->
            <div class="flex items-end justify-end space-x-4 w-full">
              <!-- Action Button -->
              <div>
                @if (expert.is_signed_in) {
                <sb-sign-out-button
                  [loading]="
                    isSubmitting() && selectedExpert()?.id === expert.id
                  "
                  [disabled]="!canPerformAction() || isSubmitting()"
                  (buttonClicked)="onSignOutClick(expert)"
                />
                } @else {
                <sb-sign-in-button
                  [loading]="
                    isSubmitting() && selectedExpert()?.id === expert.id
                  "
                  [disabled]="!canPerformAction() || isSubmitting()"
                  (buttonClicked)="onSignInClick(expert)"
                />
                }
              </div>
            </div>
          </div>
        </div>
      </div>
      }
    </div>
  `,
})
export class ExpertListComponent {
  experts = input.required<GroupedExpert[]>();
  selectedExpert = input<GroupedExpert | null>(null);
  isSubmitting = input(false);
  canPerformAction = input(true);

  expertSelect = output<GroupedExpert>();
  signIn = output<GroupedExpert>();
  signOut = output<GroupedExpert>();

  onExpertClick(expert: GroupedExpert) {
    this.expertSelect.emit(expert);
  }

  onSignInClick(expert: GroupedExpert) {
    this.signIn.emit(expert);
  }

  onSignOutClick(expert: GroupedExpert) {
    this.signOut.emit(expert);
  }

  getInitials(firstName: string, lastName: string): string {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }

  getAvatarClass(expert: GroupedExpert): string {
    return expert.is_signed_in ? 'bg-green-500' : 'bg-gray-300';
  }

  getSignInStatusText(expert: GroupedExpert): string {
    if (expert.is_signed_in) {
      return expert.active_sessions.length > 1
        ? `${expert.active_sessions.length} Sessions`
        : 'Signed In';
    }
    return 'Signed Out';
  }

  getSignInStatusBadgeClass(expert: GroupedExpert): string {
    return expert.is_signed_in
      ? 'bg-green-100 text-green-800'
      : 'bg-gray-100 text-gray-700';
  }

  getStatusBadgeClass(expert: GroupedExpert): string {
    return expert.is_active
      ? 'bg-green-100 text-green-800'
      : 'bg-gray-100 text-gray-700';
  }

  formatTime(date: Date | undefined): string {
    if (!date) return '';
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  formatDuration(minutes: number): string {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  }
}
